import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { refreshContent } from "@/hooks/useContent";
import { Save, Loader2, RotateCcw, Search, Upload, ChevronDown, ChevronRight, Image as ImageIcon, X, Link as LinkIcon } from "lucide-react";

// Friendly labels for the group prefix
const GROUP_LABELS = {
  business: "Brand & business",
  hero: "Home — hero",
  home: "Home — images & sections",
  showcase: "Home — before/after showcase",
  quote: "Home — instant estimate calculator",
  subscription: "Home — subscription block",
  process: "Home — how-it-works steps",
  cta: "Home — final CTA",
  proof: "Home — social proof bar",
  portfolio: "Portfolio page",
  about: "About page",
  booking: "Booking page",
  terms: "Booking page — terms & conditions",
  footer: "Footer",
};
const GROUP_ORDER = [
  "business", "hero", "home", "showcase", "quote", "subscription",
  "process", "proof", "cta", "portfolio", "about", "booking", "terms", "footer",
];

// Detect whether a content key represents an image URL.
function isImageKey(key, value) {
  if (/(_url|_image|_photo|portrait|hero_)/i.test(key)) return true;
  if (typeof value === "string" && /^https?:\/\/.*\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(value)) return true;
  if (typeof value === "string" && value.startsWith("/api/uploads/")) return true;
  return false;
}

function humanLabel(key) {
  // "home.showcase_before_url" → "Showcase before (image)"
  const tail = key.split(".").slice(1).join(".") || key;
  return tail
    .replace(/_url$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ContentEditor() {
  const [content, setContent] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState({}); // initialized after load

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content");
      setContent(data);
      setOriginal(data);
      // Open all groups by default on first load.
      const initial = {};
      for (const k of Object.keys(data)) {
        initial[k.split(".")[0]] = true;
      }
      setOpenGroups(initial);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changedKeys = useMemo(
    () => Object.keys(content).filter((k) => content[k] !== original[k]),
    [content, original],
  );
  const dirty = changedKeys.length > 0;

  // Group by prefix
  const groups = useMemo(() => {
    const out = {};
    const q = query.trim().toLowerCase();
    for (const key of Object.keys(content).sort()) {
      const val = content[key];
      if (q && !key.toLowerCase().includes(q) && !String(val || "").toLowerCase().includes(q)) continue;
      const prefix = key.split(".")[0];
      (out[prefix] ||= []).push(key);
    }
    return out;
  }, [content, query]);

  const orderedGroupKeys = useMemo(() => {
    const known = GROUP_ORDER.filter((g) => groups[g]?.length);
    const extra = Object.keys(groups).filter((g) => !GROUP_ORDER.includes(g)).sort();
    return [...known, ...extra];
  }, [groups]);

  const save = async () => {
    setSaving(true);
    try {
      const updates = Object.fromEntries(changedKeys.map((k) => [k, content[k]]));
      await api.put("/content", { updates }, { withCredentials: true });
      await refreshContent();
      setOriginal({ ...content });
      toast.success(`Saved ${changedKeys.length} change${changedKeys.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  const reset = () => setContent({ ...original });
  const expandAll = (open) =>
    setOpenGroups(Object.fromEntries(Object.keys(groups).map((g) => [g, open])));

  if (loading) return <div className="h-24 bg-muted rounded-xl animate-pulse" />;

  return (
    <div data-testid="content-editor" className="space-y-5">
      {/* Sticky header */}
      <div className="flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-background/80 backdrop-blur py-3 -mx-1 px-1">
        <div className="min-w-0">
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">Edit site copy</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every text + image on every page. Edit, save, and the site updates instantly.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" disabled={!dirty} onClick={reset} data-testid="content-reset" className="rounded-full">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="content-save" className="btn-primary rounded-full px-5">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save {dirty && `(${changedKeys.length})`}
          </Button>
        </div>
      </div>

      {/* Search + expand controls */}
      <Card className="p-4 border-border bg-white flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-muted-foreground" />
          <Input
            data-testid="content-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search keys or values  e.g. "concrete", "showcase", "hero"'
            className="pl-9 h-11"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
              data-testid="content-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => expandAll(true)} data-testid="content-expand-all" className="rounded-full">
          Expand all
        </Button>
        <Button variant="ghost" size="sm" onClick={() => expandAll(false)} data-testid="content-collapse-all" className="rounded-full">
          Collapse all
        </Button>
        <Badge variant="outline" data-testid="content-count">
          {Object.values(groups).reduce((n, ks) => n + ks.length, 0)} fields
        </Badge>
      </Card>

      {/* Grouped accordions */}
      {orderedGroupKeys.map((prefix) => {
        const keys = groups[prefix] || [];
        const open = openGroups[prefix] !== false;
        const title = GROUP_LABELS[prefix] || prefix.replace(/^./, (c) => c.toUpperCase());
        return (
          <Card key={prefix} className="border-border bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenGroups({ ...openGroups, [prefix]: !open })}
              data-testid={`group-toggle-${prefix}`}
              className="w-full flex items-center justify-between p-5 hover:bg-muted/40 transition-colors"
            >
              <span className="flex items-center gap-3">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-display text-lg font-semibold tracking-tight">{title}</span>
                <Badge variant="outline" className="text-xs">{keys.length}</Badge>
              </span>
              <span className="text-xs text-muted-foreground hidden sm:block font-mono">{prefix}.*</span>
            </button>
            {open && (
              <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
                {keys.map((key) => (
                  <ContentRow
                    key={key}
                    contentKey={key}
                    value={content[key]}
                    isDirty={content[key] !== original[key]}
                    originalValue={original[key]}
                    onChange={(v) => setContent({ ...content, [key]: v })}
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}

      {orderedGroupKeys.length === 0 && (
        <Card className="p-10 border-border bg-white text-center text-muted-foreground" data-testid="content-empty">
          No fields match &quot;<span className="font-mono">{query}</span>&quot;.
        </Card>
      )}
    </div>
  );
}

function ContentRow({ contentKey, value, isDirty, onChange }) {
  const isImage = isImageKey(contentKey, value);
  const isLongText = typeof value === "string" && value.length > 120;
  return (
    <div data-testid={`content-row-${contentKey}`} className="group">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <Label className="text-sm font-medium">
          {humanLabel(contentKey)}
          {isDirty && <span className="ml-2 text-xs text-amber-600">• unsaved</span>}
        </Label>
        <code className="text-[10px] text-muted-foreground font-mono opacity-60 group-hover:opacity-100">
          {contentKey}
        </code>
      </div>
      {isImage ? (
        <ImageField contentKey={contentKey} value={value} onChange={onChange} />
      ) : isLongText ? (
        <Textarea
          data-testid={`content-${contentKey}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(8, Math.max(2, Math.ceil((value || "").length / 80)))}
          className="font-normal"
        />
      ) : (
        <Input
          data-testid={`content-${contentKey}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-11"
        />
      )}
    </div>
  );
}

function ImageField({ contentKey, value, onChange }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState(value || "");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads", form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start">
      <div
        data-testid={`image-thumb-${contentKey}`}
        className="w-28 h-28 rounded-xl bg-muted border border-border overflow-hidden flex-shrink-0 grid place-items-center"
      >
        {value ? (
          <img src={value} alt={contentKey} className="w-full h-full object-cover"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0 w-full">
        {editingUrl ? (
          <div className="flex gap-2">
            <Input
              data-testid={`content-${contentKey}-url-input`}
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://… or /api/uploads/…"
              className="h-10 font-mono text-xs"
            />
            <Button
              size="sm"
              onClick={() => { onChange(urlDraft); setEditingUrl(false); }}
              data-testid={`content-${contentKey}-url-apply`}
              className="btn-primary rounded-full"
            >
              Apply
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setUrlDraft(value || ""); setEditingUrl(false); }}
              className="rounded-full"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <p
              className="text-xs text-muted-foreground font-mono break-all line-clamp-2"
              data-testid={`content-${contentKey}-current`}
            >
              {value || <span className="italic">no image set</span>}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                onChange={(e) => upload(e.target.files?.[0])}
                className="hidden"
                data-testid={`content-${contentKey}-file`}
              />
              <Button
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                data-testid={`content-${contentKey}-upload`}
                className="btn-primary rounded-full"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Upload new
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setUrlDraft(value || ""); setEditingUrl(true); }}
                data-testid={`content-${contentKey}-paste-url`}
                className="rounded-full"
              >
                <LinkIcon className="w-4 h-4 mr-1.5" />
                Paste URL
              </Button>
              {value && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange("")}
                  data-testid={`content-${contentKey}-clear`}
                  className="rounded-full text-rose-700 hover:bg-rose-50"
                >
                  <X className="w-4 h-4 mr-1.5" /> Clear
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
